import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { pilotErrorMessage } from "../lib/pilots";

// The reports of a pilot (GET /prospects/{id}/pilot/reports), newest first, loaded page by page ("before" = the date of the last one shown).
// status: loading | ready | error. A refresh keeps what is shown; a late page for another filter/prospect is dropped; more pages never duplicate a report.
export default function usePilotReports(prospectId, enabled = true, pageSize = 20) {
  const [state, setState] = useState({ status: enabled ? "loading" : "ready", items: [], hasMore: false, next: null, loadingMore: false, error: null });
  const seq = useRef(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!enabled) { setState({ status: "ready", items: [], hasMore: false, next: null, loadingMore: false, error: null }); return; }
    if (!silent) setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const { data } = await api.get(`/prospects/${prospectId}/pilot/reports`, { params: { limit: pageSize } });
      if (mine === seq.current) setState({ status: "ready", items: data.items, hasMore: !!data.has_more, next: data.next_before || null, loadingMore: false, error: null });
    } catch (e) {
      if (mine !== seq.current) return;
      setState((s) => (silent && s.status === "ready" ? s : { status: "error", items: [], hasMore: false, next: null, loadingMore: false, error: pilotErrorMessage(e, "Impossible de charger les comptes rendus.") }));
    }
  }, [prospectId, enabled, pageSize]);

  useEffect(() => {
    load();
    return () => { seq.current += 1; };
  }, [load]);

  const loadMore = useCallback(async () => {
    const before = state.next;
    if (!before || state.loadingMore) return;
    const mine = seq.current;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const { data } = await api.get(`/prospects/${prospectId}/pilot/reports`, { params: { limit: pageSize, before } });
      if (mine !== seq.current) return;
      setState((s) => {
        const seen = new Set(s.items.map((r) => r.id));
        return { ...s, items: [...s.items, ...data.items.filter((r) => !seen.has(r.id))], hasMore: !!data.has_more, next: data.next_before || null, loadingMore: false };
      });
    } catch (e) {
      if (mine === seq.current) setState((s) => ({ ...s, loadingMore: false, error: pilotErrorMessage(e, "Impossible de charger la suite.") }));
    }
  }, [prospectId, pageSize, state.next, state.loadingMore]);

  return { reports: state, reloadReports: load, loadMoreReports: loadMore };
}
